import { Component, OnDestroy, OnInit, Input, AfterViewChecked, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, switchMap, of, takeUntil, filter } from 'rxjs';
import { EditorialPageService } from './editorial-page.service';
import { IEditorialPage } from './interfaces/EditorialPage';
import * as qs from 'qs';
import { DomSanitizer } from '@angular/platform-browser';
import i18nFr from '../../../assets/i18n/fr.json';
import { StrapiContentCleanerService } from 'src/app/services/strapi-content-cleaner/strapi-content-cleaner.service';

@Component({
  selector: 'app-editorial-page',
  templateUrl: './editorial-page.component.html',
  styleUrls: ['./editorial-page.component.scss'],
})
export class EditorialPageComponent implements OnInit, OnDestroy, AfterViewChecked {
  private locale!: string;
  private subscriptionRemover = new Subject<void>();
  public editorialPage$!: Observable<IEditorialPage>;
  private page!: string;
  public showTranslationInProgress = false;
  private isContentCleaned = false;
  
  constructor(
    private translate: TranslateService,
    private router: Router,
    private editorialPageService: EditorialPageService,
    private sanitize: DomSanitizer,
    private elementRef: ElementRef,
    private strapiCleaner: StrapiContentCleanerService
  ) {
    this.router.events
      .pipe(
        takeUntil(this.subscriptionRemover),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.locale = this.translate.currentLang;
        if (this.locale === 'fr') {
          const enPage = this.router.url.split('/')[2];
          this.page = this.getObjKey(
            i18nFr.ROUTES,
            decodeURIComponent(enPage)
          ) as string;
          if (!this.page) {
            this.redirectTo404();
          }
        } else {
          this.page = this.router.url.split('/')[2];
        }
        this.isContentCleaned = false; // Reset pour permettre le nettoyage du nouveau contenu
        this.editorialPage$ = this.getEditorialPage();
      });
  }

  ngOnInit(): void {}

  ngAfterViewChecked(): void {
    // Workaround temporaire pour nettoyer le contenu Strapi
    // Voir StrapiContentCleanerService pour plus de détails
    if (!this.isContentCleaned) {
      this.cleanEmptyParagraphs();
    }
  }

  /**
   * Nettoie les paragraphes vides générés par Strapi
   * Délègue le traitement au StrapiContentCleanerService
   * 
   * TEMPORAIRE - Peut être supprimé quand Strapi sera amélioré
   */
  private cleanEmptyParagraphs(): void {
    const container = this.elementRef.nativeElement.querySelector('.editorial-container');
    if (!container) return;

    const hasCleanedSomething = this.strapiCleaner.cleanStrapiContent(container);
    
    if (hasCleanedSomething) {
      this.isContentCleaned = true;
    }
  }

  private getEditorialPage() {
    const option = qs.stringify({
      locale: this.locale,
      populate: {
        image: '*',
        page: '*',
        dynamics: {
          populate: {
            image: '*',
            video: '*',
            file: '*',
          },
        },
      },
      filters: {
        page: {
          link: {
            $eq: this.page,
          },
        },
      },
    });
    return this.editorialPageService.retrieveEditorialPage(option).pipe(
      switchMap((editorialPage) => {
        if (!editorialPage.data.length) {
          // Si on est en anglais et qu'il n'y a pas de contenu, afficher "Translation in progress"
          // au lieu de rediriger vers 404
          if (this.locale === 'en') {
            this.showTranslationInProgress = true;
            return of(null as any);
          } else {
            this.redirectTo404();
          }
        }
        this.showTranslationInProgress = false;
        return of(editorialPage.data[0]);
      })
    );
  }

  private getObjKey(routes: any, value: string) {
    return Object.keys(routes).find((key) => routes[key] === value);
  }

  private redirectTo404() {
    this.router.navigate(['404']);
  }

  ngOnDestroy(): void {
    this.subscriptionRemover.next();
    this.subscriptionRemover.complete();
  }
}
